import * as THREE from 'three';
import { Track, makeFrame, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';

/**
 * Parameterized space/void environment: starfield, nebula-tinted sky dome,
 * drifting megastructures, dual accent lighting. Several environments derive
 * from this with different palettes and props until they get bespoke builders.
 */

export interface GenericEnvParams {
  skyTop: number;
  skyBottom: number;
  starColor: number;
  nebula: number;
  structureColor: number;
  structureEmissive: number;
  hemiSky: number;
  hemiGround: number;
  sunColor: number;
  sunDir: [number, number, number];
  structureCount: number;
  structureStyle: 'rings' | 'monoliths' | 'asteroids' | 'gantries';
  floorY?: number | null;
  floorColor?: number;
}

export function buildGenericEnv(
  scene: THREE.Scene,
  track: Track,
  quality: GraphicsPreset,
  params: GenericEnvParams,
  seed = 7
): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng(seed);

  // ── sky dome: vertical gradient via shader ──
  {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        top: { value: new THREE.Color(params.skyTop) },
        bottom: { value: new THREE.Color(params.skyBottom) },
        nebula: { value: new THREE.Color(params.nebula) },
      },
      vertexShader: `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vDir;
        uniform vec3 top; uniform vec3 bottom; uniform vec3 nebula;
        // cheap value noise
        float hash(vec3 p){ return fract(sin(dot(p, vec3(127.1,311.7,74.7))) * 43758.5453); }
        void main() {
          float h = vDir.y * 0.5 + 0.5;
          vec3 col = mix(bottom, top, pow(h, 1.4));
          // nebula bands
          float n = sin(vDir.x*3.1 + vDir.y*5.2) * sin(vDir.z*2.7 - vDir.y*3.3);
          col += nebula * max(0.0, n) * 0.25 * (1.0 - h*0.5);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
  }

  // ── starfield ──
  {
    const count = quality === 'performance' ? 900 : 2200;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      // random directions on the sphere
      const t = rng.next() * Math.PI * 2;
      const z = rng.range(-1, 1);
      const r = Math.sqrt(1 - z * z);
      const R = 3500;
      pos[i * 3] = Math.cos(t) * r * R;
      pos[i * 3 + 1] = Math.abs(z) * R * (rng.chance(0.85) ? 1 : -0.3);
      pos[i * 3 + 2] = Math.sin(t) * r * R;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({
      color: params.starColor,
      size: 2.4,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
    });
    const stars = new THREE.Points(geo, mat);
    scene.add(stars);
    objects.push(stars);
    disposables.push(geo, mat);
  }

  // ── lights ──
  {
    const hemi = new THREE.HemisphereLight(params.hemiSky, params.hemiGround, 0.75);
    const sun = new THREE.DirectionalLight(params.sunColor, 1.35);
    sun.position.set(...params.sunDir).multiplyScalar(1000);
    const amb = new THREE.AmbientLight(0x223344, 0.4);
    scene.add(hemi, sun, amb);
    objects.push(hemi, sun, amb);
  }

  // ── track bounds for prop placement ──
  const frame = makeFrame();
  let cx = 0, cy = 0, cz = 0, maxR = 0;
  const SAMPLES = 60;
  const ring: { x: number; y: number; z: number }[] = [];
  for (let i = 0; i < SAMPLES; i++) {
    track.main.frameAt((i / SAMPLES) * track.length, frame);
    ring.push({ x: frame.pos.x, y: frame.pos.y, z: frame.pos.z });
    cx += frame.pos.x;
    cy += frame.pos.y;
    cz += frame.pos.z;
  }
  cx /= SAMPLES;
  cy /= SAMPLES;
  cz /= SAMPLES;
  for (const p of ring) maxR = Math.max(maxR, Math.hypot(p.x - cx, p.z - cz));

  // ── megastructures (instanced) ──
  const spinners: { mesh: THREE.InstancedMesh; speeds: number[]; axes: THREE.Vector3[] } | null =
    (() => {
      const count = quality === 'performance' ? Math.floor(params.structureCount * 0.5) : params.structureCount;
      if (count === 0) return null;
      let geo: THREE.BufferGeometry;
      switch (params.structureStyle) {
        case 'rings':
          geo = new THREE.TorusGeometry(60, 6, 8, 40);
          break;
        case 'monoliths':
          geo = new THREE.BoxGeometry(30, 160, 30);
          break;
        case 'asteroids':
          geo = new THREE.IcosahedronGeometry(28, 1);
          break;
        case 'gantries':
          geo = new THREE.BoxGeometry(120, 8, 8);
          break;
      }
      const mat = new THREE.MeshStandardMaterial({
        color: params.structureColor,
        emissive: params.structureEmissive,
        emissiveIntensity: 0.35,
        roughness: 0.7,
        metalness: 0.5,
      });
      const mesh = new THREE.InstancedMesh(geo, mat, count);
      const dummy = new THREE.Object3D();
      const speeds: number[] = [];
      const axes: THREE.Vector3[] = [];
      for (let i = 0; i < count; i++) {
        // place well outside the ribbon
        const ang = rng.range(0, Math.PI * 2);
        const dist = maxR + rng.range(180, 900);
        dummy.position.set(
          cx + Math.cos(ang) * dist,
          cy + rng.range(-260, 320),
          cz + Math.sin(ang) * dist
        );
        dummy.rotation.set(rng.range(0, 3), rng.range(0, 3), rng.range(0, 3));
        const s = rng.range(0.6, 2.6);
        dummy.scale.set(s, s, s);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        speeds.push(rng.range(-0.06, 0.06));
        axes.push(new THREE.Vector3(rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1)).normalize());
      }
      scene.add(mesh);
      objects.push(mesh);
      disposables.push(geo, mat);
      return { mesh, speeds, axes };
    })();

  // ── optional ground plane far below ──
  if (params.floorY !== null && params.floorY !== undefined) {
    const geo = new THREE.PlaneGeometry(9000, 9000, 1, 1);
    const mat = new THREE.MeshStandardMaterial({
      color: params.floorColor ?? 0x0a0d14,
      roughness: 0.95,
      metalness: 0.1,
    });
    const floor = new THREE.Mesh(geo, mat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(cx, params.floorY, cz);
    scene.add(floor);
    objects.push(floor);
    disposables.push(geo, mat);
  }

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  let frameCounter = 0;

  return {
    update(dt, _camPos, _time) {
      // slow structure drift — staggered updates, one third per frame
      if (spinners && dt > 0) {
        frameCounter++;
        const count = spinners.speeds.length;
        const start = (frameCounter % 3) * Math.ceil(count / 3);
        const end = Math.min(count, start + Math.ceil(count / 3));
        for (let i = start; i < end; i++) {
          spinners.mesh.getMatrixAt(i, m);
          m.decompose(pos, q, scl);
          q.multiply(new THREE.Quaternion().setFromAxisAngle(spinners.axes[i], spinners.speeds[i] * dt * 3));
          m.compose(pos, q, scl);
          spinners.mesh.setMatrixAt(i, m);
        }
        spinners.mesh.instanceMatrix.needsUpdate = true;
      }
    },
    dispose() {
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
